import {
  styled,
  teal,
  Paper,
  Box,
  Button,
  Link,
  Stack,
  Grid,
  Typography,
  amber,
  deepOrange,
  pink,
  cyan,
} from "fashion/fabrics";
import { YouTubeIcon, SearchIcon, VerifiedIcon } from "fashion/icons";
import CodexTierLogoClub from "nodes/codex/CodexTierLogoClub";
import CodexTierLogoDiamond from "nodes/codex/CodexTierLogoDiamond";
import CodexTierLogoSpade from "nodes/codex/CodexTierLogoSpade";
import CodexTierLogoHeart from "nodes/codex/CodexTierLogoHeart";

const LINK_HOW_TO_SEAL =
  "https://youtu.be/feSNwFdQzzo?si=N6Df4dLodzPDRa4B&t=310";
const LOGO_SIZE = 18;
const TIER_CARD_MIN_WIDTH = 160;
const TIER_CARD_MAX_WIDTH = 240;
const FORWARD_PROPS: object = {
  shouldForwardProps: (prop: string) => prop[0] !== "$",
};

const CenterStack = styled(Stack)(() => ({
  justifyContent: "space-between",
  alignItems: "center",
}));

const CircularTierPaper = styled(
  Paper,
  FORWARD_PROPS
)<{ $color: string }>(({ theme, $color }) => ({
  borderRadius: theme.spacing(1),
  height: "100%",
  padding: theme.spacing(0.5),
  minWidth: `${TIER_CARD_MIN_WIDTH}px`,
  maxWidth: `${TIER_CARD_MAX_WIDTH}px`,
  "@media print": { border: `thin solid ${theme.palette.divider}` },
  background: `linear-gradient(
    90deg ,${teal[700]} 30%,${theme.palette.background.paper} 0%) top/100% 35% no-repeat,linear-gradient(
     90deg
     ,${theme.palette.background.paper} 35%,${theme.palette.background.paper} 0%) top/100% 85% no-repeat,linear-gradient(
     90deg
     ,${theme.palette.background.paper} 20%,${$color}
   0%) top/100% 100%`,
}));

export const CircularInnerPaper = styled(Paper)(({ theme }) => ({
  background: theme.palette.background.paper,
  height: "100%",
  borderRadius: theme.spacing(1),
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  boxShadow: "none",
}));

const CodexDigitalPricing = () => (
  <Stack gap={3}>
    <Typography variant="h3" textAlign="center">
      Digital seal{" "}
      <Box component="span" color="secondary.main" display="inline">
        Pricing
      </Box>
    </Typography>
    <Typography color="text.secondary" textAlign="center">
      Release cards with a{" "}
      <Box color="secondary.main" component="span" display="inline">
        tamper proof publication seal
      </Box>{" "}
      at $0.1 per card.
    </Typography>
    <Grid container rowSpacing={2} columnSpacing={2}>
      <Grid size={{ xs: 6, sm: 3 }}>
        <CircularTierPaper elevation={8} $color={amber[700]}>
          <CircularInnerPaper>
            <CenterStack gap={1}>
              <VerifiedIcon fontSize="large" sx={{ color: "success.main" }} />
              <Typography variant="h6">+10 seals</Typography>
              <Typography variant="h5" color="success">
                $1.00
              </Typography>
              <Typography variant="body1" color="text.secondary">
                $0.1/seal
              </Typography>
              <Box width={LOGO_SIZE} height={LOGO_SIZE}>
                <CodexTierLogoClub />
              </Box>
            </CenterStack>
          </CircularInnerPaper>
        </CircularTierPaper>
      </Grid>
      <Grid size={{ xs: 6, sm: 3 }}>
        <CircularTierPaper elevation={8} $color={deepOrange[600]}>
          <CircularInnerPaper>
            <CenterStack gap={1}>
              <VerifiedIcon fontSize="large" sx={{ color: "success.main" }} />
              <Typography variant="h6">+60 seals</Typography>
              <Typography variant="h5" color="success">
                $6.00
              </Typography>
              <Typography variant="body1" color="text.secondary">
                $0.1/seal
              </Typography>
              <Box width={LOGO_SIZE} height={LOGO_SIZE}>
                <CodexTierLogoDiamond />
              </Box>
            </CenterStack>
          </CircularInnerPaper>
        </CircularTierPaper>
      </Grid>
      <Grid size={{ xs: 6, sm: 3 }}>
        <CircularTierPaper elevation={8} $color={pink[800]}>
          <CircularInnerPaper>
            <CenterStack gap={1}>
              <VerifiedIcon fontSize="large" sx={{ color: "success.main" }} />
              <Typography variant="h6">+400 seals</Typography>
              <Typography variant="h5" color="success">
                $40.00
              </Typography>
              <Typography variant="body1" color="text.secondary">
                $0.1/seal
              </Typography>
              <Box width={LOGO_SIZE} height={LOGO_SIZE}>
                <CodexTierLogoSpade />
              </Box>
            </CenterStack>
          </CircularInnerPaper>
        </CircularTierPaper>
      </Grid>
      <Grid size={{ xs: 6, sm: 3 }}>
        <CircularTierPaper elevation={8} $color={cyan[500]}>
          <CircularInnerPaper>
            <CenterStack gap={1}>
              <VerifiedIcon fontSize="large" sx={{ color: "success.main" }} />
              <Typography variant="h6">+1,200 seals</Typography>
              <Typography variant="h5" color="success">
                $120.00
              </Typography>
              <Typography variant="body1" color="text.secondary">
                $0.1/seal
              </Typography>
              <Box width={LOGO_SIZE} height={LOGO_SIZE}>
                <CodexTierLogoHeart />
              </Box>
            </CenterStack>
          </CircularInnerPaper>
        </CircularTierPaper>
      </Grid>
    </Grid>
    <Box margin="auto">
      <Button
        component={Link}
        href={LINK_HOW_TO_SEAL}
        size="large"
        variant="outlined"
        color="secondary"
        startIcon={<SearchIcon />}
        endIcon={<YouTubeIcon />}
      >
        How to seal
      </Button>
    </Box>
  </Stack>
);

export default CodexDigitalPricing;
